import React, { useEffect, useMemo, useState } from "react";
import {
    ContactMethod,
    ImportReport,
    ImportRow,
    InviteTemplate,
    Prospect,
    ProspectContactEvent,
    contactProspectByEmail,
    deleteProspect,
    getInviteTemplate,
    getProspectContacts,
    actAsOrg,
    approveClaimRequest,
    createManagedOrg,
    dismissClaimRequest,
    importProspects,
    listProspects,
    listWhyFeedback,
    checkAllPhoneTypes,
    markProspectContacted,
    WhyFeedbackEntry,
} from "../../api/ProspectAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { getCategoryById } from "../../api/CategoryAPI";
import { ParameterDataType } from "../../types/Category";
import type { Category } from "../../types/Category";
import GeoAreaPicker from "../geo/GeoAreaPicker";
import ProspectForm from "./ProspectForm";
import InventoryWorkspace from "../inventory/InventoryWorkspace";
import InviteTemplateEditor from "./InviteTemplateEditor";
import { QRCodeSVG } from "qrcode.react";
import type { CoverageArea } from "../../utils/coverage";

interface Props {
    token: string;
}

const MANUAL_METHODS: ContactMethod[] = ["PHONE", "TEXT", "WHATSAPP", "IN_PERSON", "OTHER"];

/** Substitutes the template placeholders for one prospect. The template text
 *  itself lives ONLY in the DB (seeded server-side; edited in this panel).
 *  {features} is the category-tree feature list built at compose time;
 *  {why_link} is the prospect's personalized comparison page (their own
 *  category's real filters vs keyword sites), falling back to the generic
 *  /why page when the category didn't resolve; {post_link} is their current
 *  post's URL — LINES containing it are dropped for prospects without one,
 *  so templates can reference the post without a broken sentence. */
function fillTemplate(template: string, p: Prospect, features: string): string {
    // Most personal first: their own page (their listing + their category),
    // then the category comparison, then the generic one.
    // The link must point at THIS environment — the why token only exists in
    // the DB this panel is talking to. (A prod URL composed on local/dev
    // would 404 on prod and silently degrade to the generic page.)
    const site = window.location.origin;
    const whyLink = p.whyToken
        ? `${site}/why/p/${p.whyToken}`
        : p.categoryId
        ? `${site}/why/c/${p.categoryId}`
        : `${site}/why`;
    const postLink = p.currentListingUrl?.trim() ?? "";
    const claimLink = p.claimToken ? `${site}/claim/${p.claimToken}` : "";
    let t = template;
    // Lines referencing a link the prospect doesn't have are dropped, so
    // templates can mention {post_link}/{claim_link} without broken sentences.
    for (const [tag, value] of [["{post_link}", postLink], ["{claim_link}", claimLink]] as const) {
        if (!value) {
            t = t.split("\n").filter((line) => !line.includes(tag)).join("\n")
                .replace(/\n{3,}/g, "\n\n");
        }
    }
    return t
        .replace(/\{name\}/g, p.name?.trim() || p.orgName?.trim() || "there")
        .replace(/\{company\}/g, p.orgName?.trim() || p.name?.trim() || "your business")
        .replace(/\{category\}/g, p.category?.trim() || "products or services")
        .replace(/\{features\}/g, features)
        .replace(/\{why_link\}/g, whyLink)
        .replace(/\{post_link\}/g, postLink)
        .replace(/\{claim_link\}/g, claimLink);
}

/** What the {features} placeholder becomes when the category is unresolved,
 *  has no usable parameters, or the category service is unreachable. */
const GENERIC_FEATURES = "- What you offer, prices, and the details customers filter by";

/** Turns the category's parameter definitions into a bullet list for the
 *  invite ("look what your listing can showcase"): every parameter as one
 *  bullet, in category order; choice parameters show their first choiceCount
 *  options as indented sub-bullets, with "and more" when truncated. The
 *  draft is editable, so the admin trims any bullet that doesn't sell
 *  before sending. */
function featuresFor(category: Category | null, choiceCount: number): string {
    if (!category) return GENERIC_FEATURES;
    const lines: string[] = [];
    for (const param of category.parameters ?? []) {
        if (param.dataType === ParameterDataType.SELECTABLE && (param.options ?? []).length > 0) {
            const all = param.options ?? [];
            lines.push(`- ${param.name}:`);
            for (const option of all.slice(0, choiceCount)) {
                lines.push(`    - ${option}`);
            }
            if (all.length > choiceCount) lines.push("    - and more");
        } else {
            lines.push(`- ${param.name}`);
        }
    }
    return lines.length > 0 ? lines.join("\n") : GENERIC_FEATURES;
}

/** The stored choices-per-parameter setting, defensively parsed (a hand-edited
 *  DB row shouldn't break composing). */
function choiceCountOf(template: InviteTemplate): number {
    const n = parseInt(template.featureChoiceCount, 10);
    return Number.isFinite(n) && n >= 1 ? n : 3;
}

/** Minimal CSV parser: quoted fields, embedded commas/newlines, "" escapes. */
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [], cell = "", inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
            else if (ch === '"') inQuotes = false;
            else cell += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === ",") { row.push(cell); cell = ""; }
        else if (ch === "\n" || ch === "\r") {
            if (ch === "\r" && text[i + 1] === "\n") i++;
            row.push(cell); cell = "";
            if (row.some((c) => c.trim() !== "")) rows.push(row);
            row = [];
        } else cell += ch;
    }
    row.push(cell);
    if (row.some((c) => c.trim() !== "")) rows.push(row);
    return rows;
}

/** Header-driven mapping so column order doesn't matter. */
const CSV_ALIASES: Record<string, keyof ImportRow> = {
    name: "name", contact: "name",
    org: "orgName", orgname: "orgName", company: "orgName", organization: "orgName",
    email: "email", phone: "phone", tel: "phone",
    category: "category",
    city: "city", town: "city",
    state: "state", region: "state", admin1: "state", province: "state",
    country: "country",
    notes: "notes", note: "notes",
};

function csvToImportRows(text: string, defaultCountry: string): { rows: ImportRow[]; problem?: string } {
    const parsed = parseCsv(text);
    if (parsed.length < 2) return { rows: [], problem: "Need a header row plus at least one data row." };
    const header = parsed[0].map((h) => CSV_ALIASES[h.trim().toLowerCase().replace(/[^a-z0-9]/g, "")]);
    if (!header.includes("email") || !header.includes("category")) {
        return { rows: [], problem: "Header must include at least email and category columns." };
    }
    const rows = parsed.slice(1).map((cells) => {
        const r: ImportRow = { name: "", orgName: "", email: "", phone: "", category: "",
                               city: "", state: "", country: "", notes: "" };
        header.forEach((key, i) => { if (key && cells[i] !== undefined) r[key] = cells[i].trim(); });
        if (!r.country) r.country = defaultCountry;
        return r;
    });
    return { rows };
}

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "");

/** One tri-state facet: doesn't matter / must be true / must be false. */
type Tri = "any" | "yes" | "no";

/** How the prospect can be reached — one control covering the useful
 *  combinations of email and phone, plus the cleanup view. The "only"
 *  options are exclusive (email only = email and NO phone). */
type Reach = "any" | "emailOnly" | "phoneOnly" | "either" | "both" | "neither";

/** Status filtering in three semantic groups, ANDed with each other:
 *  Reach (how to contact them), Contacted (the main workflow split), and
 *  the after-contact outcomes. The outcomes are tri-states combined per
 *  afterMode ("all" = AND; "any" = OR, e.g. bounced OR unsubscribed). */
interface StatusFilter {
    reach: Reach;
    contacted: Tri;
    opened: Tri;        // opened an invite email (tracking pixel)
    visited: Tri;       // clicked an invite link
    joined: Tri;
    unsubscribed: Tri;
    bounced: Tri;       // last email permanently bounced
    afterMode: "all" | "any";
}

const AFTER_KEYS = ["opened", "visited", "joined", "unsubscribed", "bounced"] as const;
type AfterKey = typeof AFTER_KEYS[number];

const ANY_STATUS: StatusFilter = {
    reach: "any", contacted: "any",
    opened: "any", visited: "any", joined: "any", unsubscribed: "any", bounced: "any",
    afterMode: "all",
};

/** One-click filter combinations for the everyday queries; they just set
 *  the groups, so the result can be tweaked afterwards. */
const PRESETS: [string, string, Partial<StatusFilter>][] = [
    ["To contact", "reachable, not yet contacted, not unsubscribed/bounced",
        { reach: "either", contacted: "no", unsubscribed: "no", bounced: "no" }],
    ["Follow up", "contacted but not joined (and still subscribed)",
        { contacted: "yes", joined: "no", unsubscribed: "no" }],
    ["Joined", "prospects who became members",
        { joined: "yes" }],
    ["Problems", "bounced or unsubscribed",
        { unsubscribed: "yes", bounced: "yes", afterMode: "any" }],
];

const matchesTri = (t: Tri, value: boolean) => t === "any" || (t === "yes") === value;

const matchesReach = (r: Reach, hasEmail: boolean, hasPhone: boolean): boolean => {
    switch (r) {
        case "emailOnly": return hasEmail && !hasPhone;
        case "phoneOnly": return hasPhone && !hasEmail;
        case "either": return hasEmail || hasPhone;
        case "both": return hasEmail && hasPhone;
        case "neither": return !hasEmail && !hasPhone;
        default: return true;
    }
};

const OutreachPanel: React.FC<Props> = ({ token }) => {
    const [prospects, setProspects] = useState<Prospect[]>([]);
    const [filter, setFilter] = useState<StatusFilter>(ANY_STATUS);
    const [error, setError] = useState<string | null>(null);
    // Result of the bulk phone line-type check, shown above the list; the
    // flag drives the button's hourglass while the server loops the lookups.
    const [bulkPhoneInfo, setBulkPhoneInfo] = useState<string | null>(null);
    const [checkingPhones, setCheckingPhones] = useState(false);

    // The nav menu is itself sticky (top:0, z-index 100), so the table header
    // must pin just BELOW it or it sticks invisibly underneath. Measured at
    // mount since the menu's height varies with breakpoints.
    const [stickyTop, setStickyTop] = useState(64);
    useEffect(() => {
        const menu = document.querySelector("nav.menu") as HTMLElement | null;
        if (menu) setStickyTop(menu.offsetHeight);
    }, []);
    const [busy, setBusy] = useState(false);

    // Add / edit form
    // Acting as a managed org: the listing workspace takes over the panel,
    // scoped by the act-as token (short-lived; dropping back is one click).
    const [actingAs, setActingAs] = useState<{ token: string; orgName: string } | null>(null);

    // The create/edit form takes over the whole panel while open (exclusive
    // view): { editing: null } = new prospect, { editing: p } = edit p.
    const [formFor, setFormFor] = useState<{ editing: Prospect | null } | null>(null);

    // Invite composer (email is sent by the backend; WhatsApp / SMS open the
    // chat app with the message prefilled and the admin sends it personally)
    type Channel = "EMAIL" | "WHATSAPP" | "TEXT";
    const [composeFor, setComposeFor] = useState<Prospect | null>(null);
    const [channel, setChannel] = useState<Channel>("EMAIL");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    // QR handoff: the composed text as a scannable sms:/wa.me code, so the
    // dedicated outreach phone can send it without any pairing service.
    const [qrOpen, setQrOpen] = useState(false);

    // The saved template (from DB); null until loaded — composing waits for
    // it. Editing happens in the exclusive InviteTemplateEditor panel.
    const [template, setTemplate] = useState<InviteTemplate | null>(null);
    const [templateEditorOpen, setTemplateEditorOpen] = useState(false);

    const loadTemplate = React.useCallback(() => {
        getInviteTemplate(token)
            .then(setTemplate)
            .catch((e) => { logApiError(e); setError(getUserMessage(e)); });
    }, [token]);
    useEffect(() => { loadTemplate(); }, [loadTemplate]);

    const load = async () => {
        try {
            setError(null);
            setProspects(await listProspects(token));
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };
    useEffect(() => { load(); }, [token]);

    // Coverage grid: category rows x state columns, counts per cell.
    // Client-side aggregation — the panel already holds every prospect.
    const [coverageOpen, setCoverageOpen] = useState(false);
    // Why-page reader feedback (loaded when the panel is opened).
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [feedback, setFeedback] = useState<WhyFeedbackEntry[]>([]);

    // Area filter: pick any depth; a prospect matches when its id at that
    // level equals the picked id (every row carries its full chain).
    const [areaFilter, setAreaFilter] = useState<CoverageArea | null>(null);
    const [areaFilterOpen, setAreaFilterOpen] = useState(false);
    const [statusFilterOpen, setStatusFilterOpen] = useState(false);
    const [textFilter, setTextFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");

    const visible = useMemo(() => {
        const afterValue: Record<AfterKey, (p: Prospect) => boolean> = {
            opened: (p) => p.opened,
            visited: (p) => p.clicked,
            joined: (p) => p.joined,
            unsubscribed: (p) => p.unsubscribed,
            bounced: (p) => p.bounced,
        };
        const activeAfter = AFTER_KEYS.filter((k) => filter[k] !== "any");
        let base = prospects.filter((p) => {
            if (!matchesReach(filter.reach, !!p.email?.trim(), !!p.phone?.trim())) return false;
            if (!matchesTri(filter.contacted, p.contacted)) return false;
            if (activeAfter.length === 0) return true;
            return filter.afterMode === "all"
                ? activeAfter.every((k) => matchesTri(filter[k], afterValue[k](p)))
                : activeAfter.some((k) => matchesTri(filter[k], afterValue[k](p)));
        });
        if (categoryFilter) {
            base = base.filter((p) => (p.category ?? "") === categoryFilter);
        }
        if (areaFilter) {
            const idAt: Record<string, (p: Prospect) => number | null> = {
                Country: (p) => p.countryId, Admin1: (p) => p.admin1Id,
                Admin2: (p) => p.admin2Id, City: (p) => p.cityId,
            };
            const get = idAt[areaFilter.level];
            if (get) base = base.filter((p) => get(p) === Number(areaFilter.id));
        }
        const q = textFilter.trim().toLowerCase();
        if (q) {
            base = base.filter((p) =>
                [p.name, p.orgName, p.email].some((v) => (v ?? "").toLowerCase().includes(q)));
        }
        return base;
    }, [prospects, filter, categoryFilter, areaFilter, textFilter]);

    /** State (admin1) display name, recovered from the location string we
     *  always build as "City?, County?, State, Country". */
    const admin1NameOf = (p: Prospect) => {
        const parts = (p.location ?? "").split(",").map((x) => x.trim()).filter(Boolean);
        return parts.length >= 2 ? parts[parts.length - 2] : "?";
    };

    const coverage = useMemo(() => {
        type Cell = { total: number; contacted: number; joined: number };
        const states = new Map<number, string>();          // admin1Id -> name
        const cells = new Map<string, Cell>();             // "cat|admin1Id"
        for (const p of prospects) {
            if (!p.admin1Id || !p.category?.trim()) continue;
            if (!states.has(p.admin1Id)) states.set(p.admin1Id, admin1NameOf(p));
            const key = `${p.category.trim()}|${p.admin1Id}`;
            const c = cells.get(key) ?? { total: 0, contacted: 0, joined: 0 };
            c.total++;
            if (p.contacted) c.contacted++;
            if (p.joined) c.joined++;
            cells.set(key, c);
        }
        const stateList = Array.from(states.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
        const categories = Array.from(new Set(
            prospects.map((p) => p.category?.trim()).filter((c): c is string => !!c))).sort();
        return { stateList, categories, cells };
    }, [prospects]);

    const categoriesPresent = useMemo(
        () => Array.from(new Set(prospects.map((p) => p.category?.trim()).filter((c): c is string => !!c))).sort(),
        [prospects]);

    // Contact history, loaded on demand per prospect ("history" toggle).
    const [historyFor, setHistoryFor] = useState<number | null>(null);
    const [history, setHistory] = useState<ProspectContactEvent[]>([]);

    const toggleHistory = (p: Prospect) => {
        if (historyFor === p.id) { setHistoryFor(null); return; }
        run(async () => {
            setHistory(await getProspectContacts(token, p.id));
            setHistoryFor(p.id);
        });
    };

    const run = async (fn: () => Promise<void>) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
        } catch (e) {
            logApiError(e);
            setError(getUserMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (p: Prospect) => setFormFor({ editing: p });

    const remove = (p: Prospect) => {
        if (!window.confirm(`Remove prospect "${p.name || p.orgName || p.email}"?`)) return;
        run(async () => { await deleteProspect(token, p.id); await load(); });
    };

    // Category fetches for the {features} placeholder, one per category id
    // per session (the parameter definitions don't change under us). The
    // category is cached, not the rendered text, so a changed choice-count
    // setting takes effect without a refetch.
    const featuresCache = React.useRef(new Map<string, Category | null>());

    /** The {features} text for this prospect: the category's parameters as
     *  bullets, or a generic line when unresolved. Best-effort — a
     *  category-service error just means the generic line. */
    const featuresForProspect = async (p: Prospect, t: InviteTemplate): Promise<string> => {
        if (!p.categoryId) return GENERIC_FEATURES;
        let category = featuresCache.current.get(p.categoryId);
        if (category === undefined) {
            try {
                category = await getCategoryById(token, p.categoryId);
            } catch (e) {
                logApiError(e);
                category = null;
            }
            featuresCache.current.set(p.categoryId, category);
        }
        return featuresFor(category, choiceCountOf(t));
    };

    const openCompose = async (p: Prospect, ch: Channel) => {
        if (!template) return; // still loading — buttons are disabled anyway
        const features = await featuresForProspect(p, template);
        setComposeFor(p);
        setChannel(ch);
        setQrOpen(false);
        // Email uses the long pair (service-flavored when the category is a
        // service); text/WhatsApp use the dedicated SMS template. Labeled
        // links ([words](url)) stay as-is for email (the server renders them
        // as real links) but have no hidden form in a text message — they
        // degrade to "words: url".
        if (ch === "EMAIL") {
            setSubject(fillTemplate(p.serviceCategory ? template.serviceSubject : template.subject, p, features));
            setMessage(fillTemplate(p.serviceCategory ? template.serviceMessage : template.message, p, features));
        } else {
            setSubject("");
            setMessage(fillTemplate(template.smsMessage, p, features)
                .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1: $2"));
        }
    };

    const sendInvite = () =>
        run(async () => {
            if (!composeFor) return;
            if (channel === "EMAIL") {
                await contactProspectByEmail(token, composeFor.id, subject, message, composeFor.contacted);
            } else {
                // Open the chat app with the message prefilled; the admin sends
                // it from their own number. wa.me needs digits incl. country code.
                const phone = (composeFor.phone ?? "").trim();
                const link = channel === "WHATSAPP"
                    ? `https://wa.me/${phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`
                    : `sms:${phone}?&body=${encodeURIComponent(message)}`;
                window.open(link, "_blank");
                await markProspectContacted(token, composeFor.id, channel);
            }
            setComposeFor(null);
            await load();
        });

    const markContacted = (p: Prospect, method: ContactMethod) =>
        run(async () => { await markProspectContacted(token, p.id, method); await load(); });

    // Category suggestions from the real category tree (autocomplete search);
    // free text stays allowed for categories that don't exist yet.

    // Bulk CSV import
    const [importOpen, setImportOpen] = useState(false);
    const [importText, setImportText] = useState("");
    const [importCountry, setImportCountry] = useState("United States");
    const [importReport, setImportReport] = useState<ImportReport | null>(null);

    const runImport = () =>
        run(async () => {
            setImportReport(null);
            const { rows, problem } = csvToImportRows(importText, importCountry.trim() || "United States");
            if (problem) { setError(problem); return; }
            const report = await importProspects(token, rows);
            setImportReport(report);
            if (report.added > 0) setImportText("");
            await load();
        });

    // Exclusive template editor: its own panel, nothing else competing.
    if (actingAs) {
        return (
            <div className="section-box" style={{ overflow: "visible" }}>
                <div style={{
                    display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
                    border: "2px solid #b45309", borderRadius: "8px", background: "#fffbeb",
                    padding: "0.5rem 0.75rem", marginBottom: "1rem",
                }}>
                    <strong>Acting as {actingAs.orgName}</strong>
                    <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                        managed org — everything you create or edit here belongs to them
                    </span>
                    <button className="btn-secondary" style={{ marginLeft: "auto" }}
                            onClick={() => { setActingAs(null); load(); }}>
                        Done — back to Outreach
                    </button>
                </div>
                <InventoryWorkspace token={actingAs.token} />
            </div>
        );
    }

    if (templateEditorOpen) {
        return (
            <div className="section-box" style={{ overflow: "visible" }}>
                <InviteTemplateEditor
                    token={token}
                    onDone={(saved) => {
                        setTemplateEditorOpen(false);
                        if (saved) loadTemplate(); // compose uses the fresh texts
                    }}
                />
            </div>
        );
    }

    // Exclusive form view: nothing else from the panel is shown while the
    // prospect form is open, so neither screen crowds the other.
    if (formFor) {
        return (
            <div className="section-box">
                <ProspectForm
                    token={token}
                    editing={formFor.editing}
                    onDone={(saved) => {
                        setFormFor(null);
                        if (saved) load();
                    }}
                />
            </div>
        );
    }

    return (
        // overflow:visible overrides the section-box clip — any overflow-
        // hidden/auto ancestor silently disables position:sticky, and the
        // table header sticks to the WINDOW while the page scrolls.
        <div className="section-box" style={{ overflow: "visible" }}>
            <h2>Outreach</h2>
            <div className="item-description" style={{ marginBottom: "0.75rem" }}>
                People and organizations to invite to the platform. Add them, send the
                invite email (or note a phone/text contact), and “joined” is recorded
                automatically when they register with the same email.
            </div>

            {error && <div className="error-message" style={{ marginBottom: "0.5rem" }}>{error}</div>}
            {bulkPhoneInfo && <div className="success-message" style={{ marginBottom: "0.5rem" }}>{bulkPhoneInfo}</div>}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center", marginBottom: "0.75rem" }}>
                <button onClick={() => setFormFor({ editing: null })} disabled={busy}>
                    + Add prospect
                </button>
                <button
                    className="btn-secondary"
                    onClick={() => setTemplateEditorOpen(true)}
                    disabled={busy}
                >
                    ✎ Invite template
                </button>
                <button className="btn-secondary" onClick={() => setImportOpen((o) => !o)} disabled={busy}>
                    ⬆ Import CSV
                </button>
                <button className="btn-secondary" onClick={() => setCoverageOpen((o) => !o)} disabled={busy}>
                    📊 Coverage
                </button>
                <button
                    className="btn-secondary"
                    onClick={() => {
                        const opening = !feedbackOpen;
                        setFeedbackOpen(opening);
                        if (opening) {
                            run(async () => setFeedback(await listWhyFeedback(token)));
                        }
                    }}
                    disabled={busy}
                    title="What why-page readers said (I get it / I don't get it)"
                >
                    💬 Feedback
                </button>
                <button className="btn-secondary" onClick={() => load()} disabled={busy}
                        title="Reload the prospect list (picks up joined/clicked changes)">
                    ⟳ Refresh
                </button>
                <button className="btn-secondary" disabled={busy || checkingPhones}
                        title="Look up the line type (mobile/landline) of every prospect phone that hasn't been checked yet (retries 'unknown' ones too)"
                        onClick={async () => {
                            setCheckingPhones(true);
                            try {
                                await run(async () => {
                                    const report = await checkAllPhoneTypes(token);
                                    setBulkPhoneInfo(report.checked === 0
                                        ? "All phone numbers are already checked."
                                        : `Checked ${report.checked} number${report.checked === 1 ? "" : "s"}: `
                                          + `${report.typed} typed, ${report.unknown} could not be determined (marked "unknown").`);
                                    await load();
                                });
                            } finally {
                                setCheckingPhones(false);
                            }
                        }}>
                    {checkingPhones ? "⏳ Checking numbers…" : "📞 Check numbers"}
                </button>
                <input
                    type="text"
                    value={textFilter}
                    onChange={(e) => setTextFilter(e.target.value)}
                    placeholder="Search name / org / email…"
                    style={{ width: "14rem" }}
                />
                <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} title="Filter by category">
                    <option value="">All categories</option>
                    {categoriesPresent.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                    type="button"
                    className={areaFilter ? "" : "btn-secondary"}
                    onClick={() => setAreaFilterOpen((o) => !o)}
                    title="Filter by area (any level: state, county, city)"
                >
                    🌍 {areaFilter ? `Area: ${areaFilter.name} ✕` : "Area filter"}
                </button>
                <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap" }}>
                    {(() => {
                        const active = (filter.reach !== "any" ? 1 : 0)
                            + (filter.contacted !== "any" ? 1 : 0)
                            + AFTER_KEYS.filter((k) => filter[k] !== "any").length;
                        return (
                            <button
                                type="button"
                                className={active > 0 ? "" : "btn-secondary"}
                                onClick={() => setStatusFilterOpen((o) => !o)}
                                title="Filter by status (reachability, contacted, outcomes)"
                            >
                                ⚲ Status{active > 0 ? ` (${active})` : ""} {statusFilterOpen ? "▴" : "▾"}
                            </button>
                        );
                    })()}
                    <span style={{ color: "var(--text-secondary)" }}>
                        {visible.length} of {prospects.length}
                    </span>
                </span>
            </div>

            {statusFilterOpen && (
                <div style={{
                    border: "1px solid var(--border-color, #ddd)", borderRadius: "6px",
                    padding: "0.75rem", marginBottom: "1rem",
                    display: "grid", gridTemplateColumns: "max-content 1fr",
                    columnGap: "0.9rem", rowGap: "0.6rem", alignItems: "center",
                }}>
                    {/* Presets: one click sets the groups below; tweak after. */}
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, textAlign: "right" }}>Presets:</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                        {PRESETS.map(([label, hint, patch]) => (
                            <button key={label} type="button" className="btn-sm btn-secondary"
                                    title={hint}
                                    onClick={() => setFilter({ ...ANY_STATUS, ...patch })}>
                                {label}
                            </button>
                        ))}
                        <button
                            type="button"
                            className="btn-sm btn-secondary"
                            style={{ marginLeft: "auto" }}
                            onClick={() => setFilter(ANY_STATUS)}
                        >
                            Reset all
                        </button>
                    </div>

                    {/* Group 1: how they can be reached. */}
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, textAlign: "right" }}>Reach:</span>
                    <div>
                        <select
                            value={filter.reach}
                            onChange={(e) => setFilter({ ...filter, reach: e.target.value as Reach })}
                            style={filter.reach !== "any" ? { borderColor: "var(--primary-color)" } : undefined}
                        >
                            <option value="any">anyone</option>
                            <option value="emailOnly">email only (no phone)</option>
                            <option value="phoneOnly">phone only (no email)</option>
                            <option value="either">email or phone</option>
                            <option value="both">email and phone</option>
                            <option value="neither">neither (no contact info)</option>
                        </select>
                    </div>

                    {/* Group 2: the main workflow split. */}
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, textAlign: "right" }}>Contacted:</span>
                    <div>
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: "0.4rem",
                            border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.2rem 0.5rem",
                            background: filter.contacted !== "any" ? "var(--accent-soft, #eef2ff)" : "var(--bg-secondary, #fafafa)",
                            borderColor: filter.contacted !== "any" ? "var(--primary-color, #4f46e5)" : undefined,
                        }}>
                            {(["any", "yes", "no"] as Tri[]).map((v) => (
                                <label key={v} style={{ display: "inline-flex", alignItems: "center", gap: "0.15rem", fontWeight: 400, fontSize: "0.78rem", margin: 0, cursor: "pointer" }}>
                                    <input type="radio" name="tri-contacted"
                                           checked={filter.contacted === v}
                                           onChange={() => setFilter({ ...filter, contacted: v })} />
                                    {v}
                                </label>
                            ))}
                        </span>
                    </div>

                    {/* Group 3: after-contact outcomes (all-of / any-of). */}
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, textAlign: "right", alignSelf: "start", paddingTop: "0.3rem" }}>After contact:</span>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                        {([
                            ["opened", "Opened", "opened an invite email"],
                            ["visited", "Visited", "clicked an invite link"],
                            ["joined", "Joined", "has joined"],
                            ["unsubscribed", "Unsub", "has unsubscribed"],
                            ["bounced", "Bounced", "last email permanently bounced"],
                        ] as [AfterKey, string, string][]).map(([key, label, hint]) => (
                            <span
                                key={key}
                                title={hint}
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: "0.4rem",
                                    border: "1px solid var(--border-color, #ddd)", borderRadius: "6px",
                                    padding: "0.2rem 0.5rem",
                                    background: filter[key] !== "any" ? "var(--accent-soft, #eef2ff)" : "var(--bg-secondary, #fafafa)",
                                    borderColor: filter[key] !== "any" ? "var(--primary-color, #4f46e5)" : undefined,
                                }}
                            >
                                <strong style={{ fontSize: "0.8rem" }}>{label}</strong>
                                {(["any", "yes", "no"] as Tri[]).map((v) => (
                                    <label key={v} style={{ display: "inline-flex", alignItems: "center", gap: "0.15rem", fontWeight: 400, fontSize: "0.78rem", margin: 0, cursor: "pointer" }}>
                                        <input type="radio" name={`tri-${key}`}
                                               checked={filter[key] === v}
                                               onChange={() => setFilter({ ...filter, [key]: v })} />
                                        {v}
                                    </label>
                                ))}
                            </span>
                        ))}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                            match:
                            {(["all", "any"] as const).map((m) => (
                                <label key={m} style={{ display: "inline-flex", alignItems: "center", gap: "0.15rem", fontWeight: 400, margin: 0, cursor: "pointer" }}>
                                    <input type="radio" name="after-mode"
                                           checked={filter.afterMode === m}
                                           onChange={() => setFilter({ ...filter, afterMode: m })} />
                                    {m === "all" ? "all of these" : "any of these"}
                                </label>
                            ))}
                        </span>
                    </div>
                </div>
            )}

            {feedbackOpen && (
                <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", marginBottom: "1rem" }}>
                    <strong>Why-page feedback</strong>
                    <div className="item-description" style={{ margin: "0.25rem 0 0.5rem" }}>
                        What readers clicked on the public why pages — newest first.
                    </div>
                    {feedback.length === 0 ? (
                        <p style={{ color: "var(--text-secondary)" }}>No feedback yet.</p>
                    ) : (
                        <table style={{ borderCollapse: "collapse", fontSize: "0.9rem", width: "100%" }}>
                            <tbody>
                                {feedback.map((f) => (
                                    <tr key={f.id} style={{ borderTop: "1px solid var(--border-color, #eee)" }}>
                                        <td style={{ padding: "0.35rem 0.6rem", whiteSpace: "nowrap", color: "var(--text-secondary)" }}>
                                            {f.createdAt ? new Date(f.createdAt).toLocaleString() : "—"}
                                        </td>
                                        <td style={{ padding: "0.35rem 0.6rem", whiteSpace: "nowrap", fontWeight: 700,
                                                     color: f.verdict === "GOT_IT" ? "#16a34a" : "#dc2626" }}>
                                            {f.verdict === "GOT_IT" ? "👍 gets it" : "🤔 doesn't get it"}
                                        </td>
                                        <td style={{ padding: "0.35rem 0.6rem", whiteSpace: "nowrap" }}>
                                            {f.orgName ?? "(unknown reader)"}{f.category ? ` — ${f.category}` : ""}
                                        </td>
                                        <td style={{ padding: "0.35rem 0.6rem" }}>
                                            {f.comment ?? <span style={{ color: "var(--text-secondary)" }}>no comment</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {coverageOpen && (
                <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", marginBottom: "1rem", overflowX: "auto" }}>
        <strong>Coverage — prospects / contacted / joined</strong>
        <div className="item-description" style={{ margin: "0.25rem 0 0.5rem" }}>
            Category × state. Click a cell to filter the list to it.
        </div>
        {coverage.categories.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>No located prospects yet.</p>
        ) : (
            <table style={{ borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                    <tr>
                        <th style={{ padding: "0.35rem 0.6rem", textAlign: "left" }}>Category</th>
                        {coverage.stateList.map((st) => (
                            <th key={st.id} style={{ padding: "0.35rem 0.6rem" }}>{st.name}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {coverage.categories.map((cat) => (
                        <tr key={cat} style={{ borderTop: "1px solid var(--border-color, #eee)" }}>
                            <td style={{ padding: "0.35rem 0.6rem", fontWeight: 600 }}>{cat}</td>
                            {coverage.stateList.map((st) => {
                                const c = coverage.cells.get(`${cat}|${st.id}`);
                                return (
                                    <td key={st.id}
                                        onClick={() => {
                                            if (!c) return;
                                            setCategoryFilter(cat);
                                            setAreaFilter({ level: "Admin1", id: st.id, name: st.name });
                                            setCoverageOpen(false);
                                        }}
                                        title={c ? `${cat} in ${st.name}: ${c.total} prospects, ${c.contacted} contacted, ${c.joined} joined — click to filter` : "no prospects"}
                                        style={{
                                            padding: "0.35rem 0.6rem",
                                            textAlign: "center",
                                            cursor: c ? "pointer" : "default",
                                            background: c
                                                ? (c.joined > 0 ? "var(--accent-soft, #e6f4ea)" : "var(--bg-secondary, #f9fafb)")
                                                : undefined,
                                            color: c ? undefined : "var(--text-muted, #bbb)",
                                        }}>
                                        {c ? `${c.total} / ${c.contacted} / ${c.joined}` : "—"}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
            )}

            {importOpen && (
                <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", marginBottom: "1rem", maxWidth: "720px" }}>
        <strong>Import prospects from CSV</strong>
        <div className="item-description" style={{ margin: "0.25rem 0 0.5rem" }}>
            First row must be a header. Recognized columns: name, org, email,
            phone, category, city, state, country, notes — order doesn't
            matter; email and category are required. State/country accept
            names or codes ("TX", "US"). Locations are resolved against the
            geo database; rows that can't be resolved are reported and
            skipped. Duplicate emails are skipped; existing members import
            as joined.
        </div>
        <div className="form-group" style={{ marginBottom: "0.5rem", maxWidth: "20rem" }}>
            <label>Default country (when a row has none)</label>
            <input value={importCountry} onChange={(e) => setImportCountry(e.target.value)} disabled={busy} />
        </div>
        <textarea
            rows={8}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={"name,org,email,phone,category,city,state\nJane Doe,Acme Plumbing,jane@acme.com,+1 512 555 0100,Plumbers,Austin,TX"}
            disabled={busy}
            style={{ width: "100%", fontFamily: "monospace", fontSize: "0.85rem" }}
        />
        <div className="button-group" style={{ marginTop: "0.5rem" }}>
            <button onClick={runImport} disabled={busy || !importText.trim()}>
                {busy ? "Importing…" : "Import"}
            </button>
            <button className="btn-secondary" onClick={() => { setImportOpen(false); setImportReport(null); }} disabled={busy}>
                Close
            </button>
        </div>
        {importReport && (
            <div style={{ marginTop: "0.5rem" }}>
                <div className="success-message">
                    {importReport.added} added
                    {importReport.alreadyMembers > 0 && ` (${importReport.alreadyMembers} already members)`}
                    {importReport.duplicates > 0 && ` · ${importReport.duplicates} duplicate email(s) skipped`}
                    {importReport.failed.length > 0 && ` · ${importReport.failed.length} failed`}
                </div>
                {importReport.failed.length > 0 && (
                    <ul style={{ margin: "0.35rem 0 0", paddingLeft: "1.2rem", fontSize: "0.85rem", color: "var(--danger, #b91c1c)" }}>
                        {importReport.failed.map((f, i) => (
                            <li key={i}>{f.identifier}: {f.reason}</li>
                        ))}
                    </ul>
                )}
            </div>
        )}
    </div>
            )}

            {areaFilterOpen && (
                <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", marginBottom: "1rem", maxWidth: "420px" }}>
                    <strong>Filter by area</strong>
                    <div className="item-description" style={{ margin: "0.25rem 0 0.5rem" }}>
                        Pick any depth — a state shows every prospect in it.
                    </div>
                    <GeoAreaPicker
                        token={token}
                        onChange={(chain) => {
                            const levels = chain.filter((c) => c.level !== "PostalCode");
                            setAreaFilter(levels.length ? levels[levels.length - 1] : null);
                        }}
                    />
                    <div className="button-group" style={{ marginTop: "0.5rem" }}>
                        <button className="btn-secondary" onClick={() => { setAreaFilter(null); setAreaFilterOpen(false); }}>
                            Clear area filter
                        </button>
                        <button className="btn-secondary" onClick={() => setAreaFilterOpen(false)}>
                            Close
                        </button>
                    </div>
                </div>
            )}


            {visible.length === 0 ? (
                <p style={{ color: "var(--text-secondary)" }}>No prospects here yet.</p>
            ) : (
                // No overflow wrapper here: the th cells are sticky against
                // the window, which only works when no ancestor clips or
                // scrolls (see the section-box override above).
                <div>
                    {/* overflow:visible overrides the global `table { overflow:
                        hidden }` (round-corner clipping) — that hidden is what
                        was silently disabling the sticky header. */}
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", overflow: "visible" }}>
                        <thead>
                            <tr style={{ textAlign: "left" }}>
                                {["Who", "Contact info", "Category", "Location", "Added", "Contacted", "Joined", "Actions"].map((h) => (
                                    <th key={h} style={{
                                        padding: "0.4rem",
                                        position: "sticky",
                                        top: stickyTop,
                                        zIndex: 2,
                                        background: "var(--bg-primary, #fff)",
                                        boxShadow: "0 2px 0 var(--border-color, #ddd)",
                                    }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((p) => (
                                <React.Fragment key={p.id}>
                                <tr style={{ borderBottom: "1px solid var(--border-color, #eee)", verticalAlign: "top" }}>
                                    <td style={{ padding: "0.4rem" }}>
                                        <div style={{ fontWeight: 600 }}>{p.name || "—"}</div>
                                        {p.orgName && <div style={{ color: "var(--text-secondary)" }}>{p.orgName}</div>}
                                        {p.notes && <div style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>{p.notes}</div>}
                                        {p.opened && !p.clicked && (
                                            <div style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: "0.8rem" }}
                                                 title={(p.openedAt ?? "") + " — opens are approximate (tracking pixel)"}>
                                                ✉ opened the email {fmtDate(p.openedAt)}
                                            </div>
                                        )}
                                        {p.clicked && (
                                            <div style={{ color: "var(--success, #2e7d32)", fontWeight: 600, fontSize: "0.8rem" }}
                                                 title={p.clickedAt ?? ""}>
                                                👀 visited the site {fmtDate(p.clickedAt)}
                                            </div>
                                        )}
                                        {p.unsubscribed && (
                                            <div style={{ color: "var(--danger, #b91c1c)", fontWeight: 600, fontSize: "0.8rem" }}
                                                 title={p.unsubscribedAt ?? ""}>
                                                🚫 unsubscribed {fmtDate(p.unsubscribedAt)}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: "0.4rem" }}>
                                        {p.email && (
                                            <div>
                                                {p.email}
                                                {p.bounced && (
                                                    <span title={`Permanently bounced${p.bouncedAt ? " on " + p.bouncedAt.slice(0, 10) : ""} — edit the address to clear`}
                                                          style={{ marginLeft: "0.35rem", fontSize: "0.7rem", padding: "0.05rem 0.3rem", borderRadius: "4px", background: "#dc2626", color: "#fff" }}>
                                                        bounced
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {p.phone && (
                                            <div>
                                                {p.phone}
                                                {p.phoneType && (
                                                    <span title={p.phoneType === "MOBILE" ? "Mobile — can receive SMS. " + (p.phoneCarrier ?? "")
                                                              : p.phoneType === "LANDLINE" ? "Cannot receive SMS/WhatsApp. " + (p.phoneCarrier ?? "")
                                                              : p.phoneType === "UNKNOWN" ? "Line type could not be determined — the number may be wrong"
                                                              : (p.phoneCarrier ?? "")}
                                                          style={{ marginLeft: "0.35rem", fontSize: "0.7rem", padding: "0.05rem 0.3rem", borderRadius: "4px",
                                                                   background: p.phoneType === "MOBILE" ? "#16a34a"
                                                                             : p.phoneType === "LANDLINE" || p.phoneType === "INVALID" ? "#dc2626"
                                                                             : p.phoneType === "UNKNOWN" ? "#d97706" : "#6b7280",
                                                                   color: "#fff" }}>
                                                        {p.phoneType === "MOBILE" ? "mobile ✓"
                                                            : p.phoneType === "LANDLINE" ? "landline — no SMS"
                                                            : p.phoneType.toLowerCase()}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: "0.4rem" }}>
                                        {p.category || ""}
                                        {p.serviceCategory && (
                                            <span style={{ marginLeft: "0.35rem", fontSize: "0.7rem", padding: "0.05rem 0.3rem", borderRadius: "4px", background: "var(--primary-color)", color: "#fff" }}>
                                                service
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ padding: "0.4rem" }}>{p.location || "—"}</td>
                                    <td style={{ padding: "0.4rem" }} title={p.createdAt ?? ""}>{fmtDate(p.createdAt)}</td>
                                    <td style={{ padding: "0.4rem" }}>
                                        {p.contacted ? (
                                            <span title={p.contactedAt ?? ""}>
                                                ✓ {p.contactMethod?.replace("_", " ").toLowerCase()} {fmtDate(p.contactedAt)}
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--text-secondary)" }}>not yet</span>
                                        )}
                                        {p.contacted && (
                                            <div>
                                                <button className="btn-sm" onClick={() => toggleHistory(p)} disabled={busy}
                                                        style={{ marginTop: "0.2rem" }}>
                                                    {historyFor === p.id ? "hide history" : "history…"}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: "0.4rem" }}>
                                        {p.joined ? (
                                            <span style={{ color: "var(--success, #2e7d32)", fontWeight: 600 }} title={p.joinedAt ?? ""}>
                                                ✓ {fmtDate(p.joinedAt)}{p.orgId ? ` (org ${p.orgId})` : ""}
                                            </span>
                                        ) : (
                                            <span style={{ color: "var(--text-secondary)" }}>no</span>
                                        )}
                                    </td>
                                    <td style={{ padding: "0.4rem", whiteSpace: "nowrap" }}>
                                        <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                                            {p.email && (
                                                <button className="btn-sm" onClick={() => openCompose(p, "EMAIL")}
                                                        disabled={busy || !template || p.unsubscribed || p.joined || p.bounced}
                                                        title={p.unsubscribed ? "Unsubscribed — emailing is blocked"
                                                             : p.bounced ? "Email bounced — edit the address to clear this"
                                                             : p.joined ? "Already joined — no invite needed"
                                                             : p.contacted ? "Send the invite again (already contacted"
                                                                   + (p.contactedAt ? " on " + p.contactedAt.slice(0, 10) : "") + ")"
                                                             : undefined}>
                                                    {p.contacted ? "↻ Resend" : "✉ Email"}
                                                </button>
                                            )}
                                            {p.phone && (
                                                <button className="btn-sm" onClick={() => openCompose(p, "WHATSAPP")}
                                                        disabled={busy || !template || p.joined || p.contacted
                                                            || p.phoneType === "LANDLINE" || p.phoneType === "INVALID"}
                                                        title={p.phoneType === "LANDLINE" ? "Landline — cannot receive WhatsApp"
                                                             : p.phoneType === "INVALID" ? "Invalid number — check the phone"
                                                             : p.joined ? "Already joined — no invite needed"
                                                             : p.contacted ? "Already contacted — repeat invites are blocked"
                                                             : undefined}>
                                                    💬 WhatsApp
                                                </button>
                                            )}
                                            {p.phone && (
                                                <button className="btn-sm" onClick={() => openCompose(p, "TEXT")}
                                                        disabled={busy || !template || p.joined || p.contacted
                                                            || p.phoneType === "LANDLINE" || p.phoneType === "INVALID"}
                                                        title={p.phoneType === "LANDLINE" ? "Landline — cannot receive SMS"
                                                             : p.phoneType === "INVALID" ? "Invalid number — check the phone"
                                                             : p.joined ? "Already joined — no invite needed"
                                                             : p.contacted ? "Already contacted — repeat invites are blocked"
                                                             : undefined}>
                                                    📱 SMS
                                                </button>
                                            )}
                                            <select
                                                value=""
                                                onChange={(e) => {
                                                    if (e.target.value) markContacted(p, e.target.value as ContactMethod);
                                                    e.target.value = "";
                                                }}
                                                disabled={busy}
                                                title="Record a contact made outside the app"
                                            >
                                                <option value="">Mark contacted…</option>
                                                {MANUAL_METHODS.map((m) => (
                                                    <option key={m} value={m}>{m.replace("_", " ").toLowerCase()}</option>
                                                ))}
                                            </select>
                                            <button className="btn-sm" onClick={() => startEdit(p)} disabled={busy}>Edit</button>
                                            <button className="btn-sm" onClick={() => remove(p)} disabled={busy}>Delete</button>
                                            {p.whyToken && (
                                                <a className="btn-sm" href={`/why/p/${p.whyToken}`} target="_blank" rel="noopener noreferrer"
                                                   title="Their personalized comparison page — what {why_link} becomes in invite emails">
                                                    Why page ↗
                                                </a>
                                            )}
                                            {p.orgId == null ? (
                                                <button className="btn-sm" disabled={busy}
                                                        title="Create a managed org + store for them (no account, no password — BabaHanks stewards it until they claim it)"
                                                        onClick={() => run(async () => {
                                                            const updated = await createManagedOrg(token, p.id);
                                                            setProspects((list) => list.map((x) => x.id === updated.id ? updated : x));
                                                        })}>
                                                    🏪 Create org
                                                </button>
                                            ) : !p.joined ? (
                                                <button className="btn-sm" disabled={busy}
                                                        title="Open the listing workspace acting as their managed org"
                                                        onClick={() => run(async () => {
                                                            const a = await actAsOrg(token, p.id);
                                                            setActingAs({ token: a.token, orgName: a.orgName });
                                                        })}>
                                                    🏪 Manage listings
                                                </button>
                                            ) : (
                                                <span className="btn-sm" style={{ color: "#16a34a", fontWeight: 700, border: "none" }}
                                                      title="They own their org now — it is theirs to manage">
                                                    claimed ✓
                                                </span>
                                            )}
                                            {p.claimRequestEmail && (
                                                <span style={{
                                                    display: "inline-flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap",
                                                    border: "1px solid #b45309", borderRadius: "6px", background: "#fffbeb",
                                                    padding: "0.15rem 0.5rem", fontSize: "0.85rem",
                                                }}
                                                      title={`Requested ${p.claimRequestAt ? new Date(p.claimRequestAt).toLocaleString() : ""} from the public listing — verify before approving`}>
                                                    <strong>claim request:</strong>
                                                    {p.claimRequestName ? `${p.claimRequestName} — ` : ""}{p.claimRequestEmail}
                                                    <button className="btn-sm" disabled={busy}
                                                            title="Email the take-ownership link to this address"
                                                            onClick={() => run(async () => {
                                                                const updated = await approveClaimRequest(token, p.id);
                                                                setProspects((list) => list.map((x) => x.id === updated.id ? updated : x));
                                                            })}>
                                                        ✓ Approve & send
                                                    </button>
                                                    <button className="btn-sm btn-secondary" disabled={busy}
                                                            onClick={() => run(async () => {
                                                                const updated = await dismissClaimRequest(token, p.id);
                                                                setProspects((list) => list.map((x) => x.id === updated.id ? updated : x));
                                                            })}>
                                                        Dismiss
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                                {composeFor?.id === p.id && (
                                    <tr style={{ borderBottom: "1px solid var(--border-color, #eee)" }}>
                                        <td colSpan={8} style={{ padding: "0.5rem 0.4rem 0.75rem 0.75rem" }}>
                        <div style={{ border: "1px solid var(--primary-color)", borderRadius: "6px", padding: "0.75rem", marginBottom: "1rem", maxWidth: "640px" }}>
                            <strong>
                                {channel === "EMAIL" && `Invite email to ${composeFor.email}`}
                                {channel === "WHATSAPP" && `WhatsApp invite to ${composeFor.phone}`}
                                {channel === "TEXT" && `Text (SMS) invite to ${composeFor.phone}`}
                            </strong>
                            {channel === "EMAIL" && composeFor.contacted && (
                                <div className="item-description" style={{ margin: "0.35rem 0", color: "#b45309" }}>
                                    ⚠ This prospect was already contacted
                                    {composeFor.contactedAt ? ` on ${composeFor.contactedAt.slice(0, 10)}` : ""} — sending
                                    again will deliver a second email.
                                </div>
                            )}
                            {channel === "EMAIL" ? (
                                <div className="form-group" style={{ margin: "0.5rem 0" }}>
                                    <label>Subject</label>
                                    <input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} />
                                </div>
                            ) : (
                                <div className="item-description" style={{ margin: "0.35rem 0" }}>
                                    This opens {channel === "WHATSAPP" ? "WhatsApp" : "your messages app"} with
                                    the text prefilled — you send it from your own number, and the prospect is
                                    marked contacted here.
                                    {channel === "WHATSAPP" && " The phone number must include the country code."}
                                </div>
                            )}
                            <div className="form-group" style={{ marginBottom: "0.5rem" }}>
                                <label>Message</label>
                                <textarea rows={channel === "EMAIL" ? 10 : 5} value={message} onChange={(e) => setMessage(e.target.value)} disabled={busy} />
                                {channel !== "EMAIL" && (
                                    <div style={{ fontSize: "0.8rem", marginTop: "0.2rem", color: message.length > 450 ? "#b45309" : "var(--text-secondary)" }}>
                                        {message.length} characters (~{Math.max(1, Math.ceil(message.length / 153))} SMS segments)
                                        {message.length > 450 && " — long texts may be refused by carriers; keep it short"}
                                    </div>
                                )}
                            </div>
                            {channel !== "EMAIL" && (
                                <div style={{ marginBottom: "0.5rem" }}>
                                    <button type="button" className="btn-sm" onClick={() => setQrOpen((o) => !o)} disabled={busy}>
                                        {qrOpen ? "Hide QR" : "📱 QR — send from the outreach phone"}
                                    </button>
                                    {qrOpen && composeFor.phone && (() => {
                                        // SMSTO: carries the text raw (no percent-encoding), which
                                        // fits ~3x more message than an sms:?body= URI; wa.me must
                                        // stay a URL. A QR tops out near 2950 bytes — past that the
                                        // encoder throws, so degrade to a hint instead.
                                        const qrValue = channel === "WHATSAPP"
                                            ? `https://wa.me/${composeFor.phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`
                                            : `SMSTO:${composeFor.phone.trim()}:${message}`;
                                        if (qrValue.length > 2800) {
                                            return (
                                                <div className="error-message" style={{ marginTop: "0.5rem", maxWidth: "28rem" }}>
                                                    This message is too long to fit in a QR code
                                                    ({qrValue.length} of ~2800 characters). Shorten the
                                                    message — or send it with the button below instead.
                                                </div>
                                            );
                                        }
                                        return (
                                        <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.75rem", alignItems: "flex-start", flexWrap: "wrap" }}>
                                            {/* Live-updates with the message; low error-correction keeps
                                                the code coarse enough to scan off a screen even with a
                                                long {why_link} message. */}
                                            <QRCodeSVG
                                                value={qrValue}
                                                size={280}
                                                level="L"
                                                marginSize={2}
                                            />
                                            <div style={{ maxWidth: "20rem", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                                                Scan with the outreach phone's camera —{" "}
                                                {channel === "WHATSAPP" ? "WhatsApp" : "its Messages app"} opens with the
                                                recipient and this text filled in. Send it there, then press the button
                                                below so the prospect is recorded as contacted.
                                                <div style={{ marginTop: "0.5rem" }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => run(async () => {
                                                            await markProspectContacted(token, composeFor.id, channel);
                                                            setComposeFor(null);
                                                            await load();
                                                        })}
                                                        disabled={busy}
                                                    >
                                                        Sent from the phone — mark contacted
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })()}
                                </div>
                            )}
                            <div className="button-group">
                                <button
                                    onClick={sendInvite}
                                    disabled={busy || !message.trim() || (channel === "EMAIL" && !subject.trim())}
                                >
                                    {busy ? "Working…"
                                        : channel === "EMAIL" ? "Send invite"
                                        : channel === "WHATSAPP" ? "Open WhatsApp"
                                        : "Open messages app"}
                                </button>
                                <button className="btn-secondary" onClick={() => setComposeFor(null)} disabled={busy}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                                        </td>
                                    </tr>
                                )}
                                {historyFor === p.id && (
                                    <tr style={{ borderBottom: "1px solid var(--border-color, #eee)" }}>
                                        <td colSpan={8} style={{ padding: "0.4rem 0.4rem 0.6rem 1.5rem", background: "var(--bg-secondary, #f9fafb)" }}>
                                            {history.length === 0 ? (
                                                <span style={{ color: "var(--text-secondary)" }}>
                                                    No recorded contacts. (Contacts made before the history feature aren't listed.)
                                                </span>
                                            ) : (
                                                <ul style={{ margin: 0, paddingLeft: "1rem", fontSize: "0.85rem" }}>
                                                    {history.map((c) => (
                                                        <li key={c.id}>
                                                            {c.contactedAt ? new Date(c.contactedAt).toLocaleString() : "—"}
                                                            {" — "}{c.method?.replace("_", " ").toLowerCase()}
                                                            {c.subject && <span style={{ color: "var(--text-secondary)" }}> — “{c.subject}”</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OutreachPanel;